(function () {
  function pad(value) {
    return String(value).padStart(2, "0");
  }

  function toYMD(date) {
    return (
      date.getFullYear() +
      "-" +
      pad(date.getMonth() + 1) +
      "-" +
      pad(date.getDate())
    );
  }

  function toMD(date) {
    return pad(date.getMonth() + 1) + "-" + pad(date.getDate());
  }

  function cloneDate(date) {
    return new Date(date.getTime());
  }

  function startOfDay(date) {
    const d = cloneDate(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  function parseCutoff(cutoffTime) {
    const parts = (cutoffTime || "14:00").split(":");
    return {
      hours: parseInt(parts[0], 10) || 14,
      minutes: parseInt(parts[1], 10) || 0
    };
  }

  function isHoliday(date, holidays) {
    const ymd = toYMD(date);
    const md = toMD(date);
    return (holidays || []).includes(ymd) || (holidays || []).includes(md);
  }

  function isWeekend(date) {
    const day = date.getDay();
    return day === 0 || day === 6;
  }

  function isBusinessDay(date, holidays) {
    return !isWeekend(date) && !isHoliday(date, holidays);
  }

  function nextBusinessDay(date, holidays) {
    const d = startOfDay(date);
    while (!isBusinessDay(d, holidays)) {
      d.setDate(d.getDate() + 1);
    }
    return d;
  }

  function addBusinessDays(date, days, holidays) {
    const d = startOfDay(date);

    if (days === 0) {
      return nextBusinessDay(d, holidays);
    }

    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      if (isBusinessDay(d, holidays)) {
        added++;
      }
    }
    return d;
  }

  function getTimeZoneDateParts(date, timeZone) {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23"
    });

    const parts = formatter.formatToParts(date);
    const map = {};

    parts.forEach(function (part) {
      if (part.type !== "literal") {
        map[part.type] = part.value;
      }
    });

    return {
      year: parseInt(map.year, 10),
      month: parseInt(map.month, 10),
      day: parseInt(map.day, 10),
      hour: parseInt(map.hour, 10),
      minute: parseInt(map.minute, 10),
      second: parseInt(map.second, 10)
    };
  }

  function getDateInTimeZone(date, timeZone) {
    const parts = getTimeZoneDateParts(date, timeZone);
    return new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  }

  function getShipDate(now, rule) {
    const holidays = rule.holidays || [];
    const timeZone = rule.timeZone || "Europe/Amsterdam";
    const zonedNow = getDateInTimeZone(now, timeZone);

    const today = startOfDay(zonedNow);
    const cutoff = parseCutoff(rule.cutoffTime);

    const cutoffDate = cloneDate(today);
    cutoffDate.setHours(cutoff.hours, cutoff.minutes, 0, 0);

    const todayIsBusinessDay = isBusinessDay(today, holidays);
    const beforeCutoff = zonedNow.getTime() <= cutoffDate.getTime();

    if (todayIsBusinessDay && beforeCutoff) {
      return today;
    }

    const nextDay = cloneDate(today);
    nextDay.setDate(nextDay.getDate() + 1);
    return nextBusinessDay(nextDay, holidays);
  }

  function formatDate(date, locale) {
    return new Intl.DateTimeFormat(locale || "nl-NL", {
      weekday: "long",
      day: "numeric",
      month: "long"
    }).format(date);
  }

  function capitalize(str) {
    if (!str || typeof str !== "string") return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  function getCountryRule(settings, countryCode) {
    const code = (countryCode || "").toUpperCase();
    return (
      settings.countries[code] ||
      settings.countries[settings.defaultCountry] ||
      null
    );
  }

  function calculateEstimate(countryCode, settings) {
    const rule = getCountryRule(settings, countryCode);
    if (!rule) return null;

    const now = new Date();
    const shipDate = getShipDate(now, rule);

    const earliest = addBusinessDays(shipDate, rule.transitMin, rule.holidays || []);
    const latest = addBusinessDays(shipDate, rule.transitMax, rule.holidays || []);

    return {
      shipDate: shipDate,
      earliest: earliest,
      latest: latest,
      isRange: rule.transitMin !== rule.transitMax
    };
  }

  function renderEstimate() {
    const container = document.getElementById("estimated-delivery");
    if (!container || !window.deliveryEstimateSettings) return;

    const valueEl = container.querySelector(".estimated-delivery__value");
    if (!valueEl) return;

    const settings = window.deliveryEstimateSettings;
    const locale = container.dataset.locale || "nl-NL";
    const countryCode = (container.dataset.country || settings.defaultCountry || "NL").toUpperCase();

    const result = calculateEstimate(countryCode, settings);

    if (!result) {
      valueEl.textContent = "Niet beschikbaar";
      return;
    }

    if (settings.displayMode === "range" && result.isRange) {
      valueEl.textContent =
        capitalize(formatDate(result.earliest, locale)) +
        " – " +
        capitalize(formatDate(result.latest, locale));
      return;
    }

    valueEl.textContent = capitalize(formatDate(result.earliest, locale));
  }

  function initTooltip() {
    const container = document.getElementById("estimated-delivery");
    if (!container) return;

    const button = container.querySelector(".estimated-delivery__info-button");
    const tooltip = container.querySelector(".estimated-delivery__tooltip");

    if (!button || !tooltip || button.dataset.initialized === "true") return;

    function openTooltip() {
      tooltip.classList.add("is-visible");
      tooltip.setAttribute("aria-hidden", "false");
      button.setAttribute("aria-expanded", "true");
    }

    function closeTooltip() {
      tooltip.classList.remove("is-visible");
      tooltip.setAttribute("aria-hidden", "true");
      button.setAttribute("aria-expanded", "false");
    }

    function toggleTooltip(event) {
      event.stopPropagation();

      if (tooltip.classList.contains("is-visible")) {
        closeTooltip();
      } else {
        openTooltip();
      }
    }

    button.addEventListener("click", toggleTooltip);

    document.addEventListener("click", function (event) {
      if (!container.contains(event.target)) {
        closeTooltip();
      }
    });

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape") {
        closeTooltip();
      }
    });

    button.dataset.initialized = "true";
  }

  function init() {
    renderEstimate();
    initTooltip();
  }

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("shopify:section:load", init);
  document.addEventListener("shopify:section:reorder", init);
  document.addEventListener("shopify:section:select", init);
})();